import React, { createContext, useContext, useState, useCallback } from 'react';
import { ButtonColors } from 'ui/elements/buttons/Button';

export type SnackbarVariant = 'success' | 'error';
export type SnackbarAction = {
  color: ButtonColors;
  text: string;
  onClick: () => void;
};

interface NotificationState {
  open: boolean;
  message: string;
  variant: SnackbarVariant;
  action?: SnackbarAction;
  durationInMs: number;
}

interface NotificationContextType {
  notification: NotificationState;
  notify: (type: SnackbarVariant, message: string, action?: SnackbarAction) => void;
  closeNotification: () => void;
}

const initialNotification: NotificationState = {
  open: false,
  message: '',
  variant: 'success',
  durationInMs: 4000,
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    console.error('NotificationContext is undefined. Component may be outside NotificationProvider.');
    console.trace('Context usage stack trace:');
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider. Check that your component is wrapped in <NotificationProvider>',
    );
  }
  return context;
};

export const useNotifyContext = () => {
  const { notify } = useNotificationContext();
  return notify;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationState>(initialNotification);

  const notify = useCallback((type: SnackbarVariant, message: string, action?: SnackbarAction) => {
    // Calculate duration based on message length (same logic as Redux version)
    const duration = Math.max(4000, Math.min(10000, 100 * message.length));

    setNotification({
      open: true,
      message,
      variant: type,
      action,
      durationInMs: duration,
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notification,
        notify,
        closeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
